import React from "react";
import { memberAvatarPair } from "./tokens";

// =====================================================================
// Club Radar V2 — identity atoms (DESIGN-SPEC §3 AVATAR-SQ, CREST,
// AVATAR-STACK; §4 items 10/20). Purely presentational; real data
// (photo/logo URLs, names) drives them — artboard initials/colors are
// sample assignments.
//
// Reuse note: the existing shared atoms (agent-radar/RadarAtoms
// RadarAvatar/RadarCrest) render CIRCLES with alpha-tint recipes — no
// true visual match to the artboard's ROUNDED-SQUARE avatars and white
// bordered crest chips, so these are new under the club-radar v2
// namespace (Brief Rule 1: the artboard wins).
// =====================================================================

export const initialsOf = (name, max = 2) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, max)
    .toUpperCase();

// ── AVATAR-SQ — person/entity avatar ─────────────────────────────────
// Rounded square, bg `#EEF2F7` fg `#475569` (or colored pairs), initials
// 700/800; sizes 24–58, radius scales 7–16 per frame. A photo wins when
// the API supplies one.
export const AvatarSquare = ({
  name,
  initials,
  imageUrl,
  size = 36,
  radius = 10,
  fontSize,
  fontWeight = 700,
  bg = "#EEF2F7",
  fg = "#475569",
  style,
  ...rest
}) => {
  const base = {
    width: size,
    height: size,
    borderRadius: radius,
    flex: "none",
    ...style,
  };
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        referrerPolicy="no-referrer"
        style={{ ...base, objectFit: "cover" }}
        {...rest}
      />
    );
  }
  return (
    <div
      style={{
        ...base,
        background: bg,
        color: fg,
        fontWeight,
        fontSize: fontSize || Math.round(size / 3),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      {...rest}
    >
      {initials || initialsOf(name)}
    </div>
  );
};

// ── CREST — club crest fallback chip ─────────────────────────────────
// Square 15–17px, r4–5, bg `#fff`, border 1px `#E5E7EB`, initials
// 7–7.5px/800 `#6B7280`; the club logo wins when present.
export const CrestChip = ({
  name,
  initials,
  logoUrl,
  size = 16,
  radius = 4,
  fontSize = 7.5,
  style,
  ...rest
}) => {
  const base = {
    width: size,
    height: size,
    borderRadius: radius,
    flex: "none",
    ...style,
  };
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        referrerPolicy="no-referrer"
        style={{ ...base, objectFit: "cover" }}
        {...rest}
      />
    );
  }
  return (
    <div
      style={{
        ...base,
        background: "#fff",
        border: "1px solid #E5E7EB",
        boxSizing: "border-box",
        color: "#6B7280",
        fontSize,
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      {...rest}
    >
      {initials || initialsOf(name)}
    </div>
  );
};

// ── AVATAR-STACK — overlapping member circles (1b meta row, 2c) ──────
// Circles 20×20 (2c) / 22×22 (1b), border 2px `#fff`, overlap −6/−7px,
// initials 8/8.5px 800. `members`: [{name, initials?, imageUrl?, bg?,
// fg?}] — explicit pairs win, else deterministic rotation through the
// artboard palette. Real counts drive `max`/overflow — sample stacks of
// four are illustrative, never a cap. Optional `total` (backward-
// compatible, default = members.length) lets callers pass the REAL
// member count when `members` is only a preview subset (e.g. the list
// API's shared_member_preview + shared_member_count) so the "+N"
// overflow stays truthful.
export const AvatarStack = ({ members = [], size = 22, max = 4, total, style, ...rest }) => {
  const overlap = size >= 22 ? -7 : -6;
  const fontSize = size >= 22 ? 8.5 : 8;
  const shown = members.slice(0, max);
  const overflow = Math.max(0, (total ?? members.length) - shown.length);
  const circle = {
    width: size,
    height: size,
    borderRadius: "50%",
    border: "2px solid #fff",
    boxSizing: "content-box",
    fontSize,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "none",
  };
  return (
    <div style={{ display: "flex", alignItems: "center", ...style }} {...rest}>
      {shown.map((m, i) => {
        const pair = m.bg && m.fg ? { bg: m.bg, fg: m.fg } : memberAvatarPair(m.name || m.initials);
        return m.imageUrl ? (
          <img
            key={m.id || m.name || i}
            src={m.imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            style={{ ...circle, objectFit: "cover", marginLeft: i === 0 ? 0 : overlap }}
          />
        ) : (
          <div
            key={m.id || m.name || i}
            style={{
              ...circle,
              background: pair.bg,
              color: pair.fg,
              marginLeft: i === 0 ? 0 : overlap,
            }}
          >
            {m.initials || initialsOf(m.name)}
          </div>
        );
      })}
      {overflow > 0 && (
        <div
          style={{
            ...circle,
            background: "#EEF2F7",
            color: "#6B7280",
            marginLeft: shown.length ? overlap : 0,
          }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
};
